import { Box, Grid, Divider } from '@mui/material';
import PropTypes from 'prop-types';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { updateMessageCountById } from '../../../../redux/slices/orders';
import { useDispatch } from '../../../../redux/store';
import Lightbox from '../../../../reusable-components/lightbox';
import OrderNotesItem from './OrderNotesItem';
import Editor from '../../../../reusable-components/editor';
import { ticketMessageInput, ticketMessageSubmitAllPictures, ticketMessageSubmitHtmlFormatter } from './newTicket/ticket-message-input-cleanup';
import axiosInstance from '../../../../utils/axios';
import { postSendTicketMessage } from '../../../../api/tickets';
import { getAllImagesForLighthouse, handleAddTicketAttachment, getImageID } from './newTicket/TicketHelper';
import TicketAttachmentThumbnailStack from './TicketAttachmentThumbnailStack';
import LoadingAnimation from '../../../../reusable-components/loader/LoadingAnimation';

OrderNotesList.propTypes = {
  id: PropTypes.number,
};

export default function OrderNotesList({ id }) {
  const dispatch = useDispatch();

  const [selectedImage, setSelectedImage] = useState(-1);
  const [messages, setMessages] = useState([]);
  const [orderNoteTicketId, setOrderNoteTicketId] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    setLoading(true);
    axiosInstance.get(`/TicketOrderNoteMessages?orderId=${id}`)
      .then(res => {
        setMessages(res.data?.messages ?? []);
        setOrderNoteTicketId(res.data?.orderNoteTicketId ?? 0);
      })
      .catch(() => enqueueSnackbar("Failed to load notes", { variant: "error" }))
      .finally(() => {
        setLoading(false);
        setTimeout(() => {
          const listElement = document.getElementById('order-note-end');
          listElement?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }, 100);
      });
  }, []);

  const imagesLightbox = getAllImagesForLighthouse(messages);
  const handleOpenLightbox = (imageUrl) => setSelectedImage(imagesLightbox?.findIndex((image) => getImageID(image.src) === getImageID(imageUrl)) ?? -1);
  const handleChangeMessage = (value) => ticketMessageInput(value, setMessage);

  const handleSend = async () => {
    if (!message && attachments?.length === 0) return;
    try {
      setSendingMessage(true);
      const messageWithPics = await ticketMessageSubmitAllPictures(ticketMessageSubmitHtmlFormatter(message));
      const res = await postSendTicketMessage({ message: messageWithPics, ticketNumber: orderNoteTicketId, files: attachments, notify: false, isOrderNote: true });
      setMessages(res.data ?? []);
      setMessage('');
      setAttachments([]);
      dispatch(updateMessageCountById(id));
    } catch {
      enqueueSnackbar("Failed to save note", { variant: "error" })
    } finally {
      setSendingMessage(false);
    }
  };

  return <Box sx={{ padding: 1 }}>
    {loading ? <LoadingAnimation /> : <>
      <Grid maxWidth={256} overflow="auto" container direction="column" justifyContent="flex-start" alignItems="flex-start">
        {messages.map((message, index) => <OrderNotesItem
          key={index}
          message={message}
          previousMessage={messages[index - 1]}
          fileArray={message.file ? [message] : null}
          onOpenLightbox={(imgUrl) => handleOpenLightbox(imgUrl)}
        />)}
        <Lightbox index={selectedImage} slides={imagesLightbox} open={selectedImage >= 0} close={() => setSelectedImage(-1)} />
      </Grid>

      <Box sx={{ mt: 1 }} >
        <Divider sx={{ borderColor: 'info.main', borderWidth: 2, mb: 1}} />
        <TicketAttachmentThumbnailStack attachments={attachments} setAttachments={setAttachments} />

        <Editor
          attachFile
          verySimple
          id="ticket-reply"
          value={message}
          onChange={handleChangeMessage}
          placeholder="Add note..."
          sx={{ flexGrow: 1, borderColor: 'transparent' }}
          sendIcon
          handleSend={handleSend}
          handleFileInputChange={(event) => handleAddTicketAttachment(event, attachments, setAttachments)}
          sendingTicketMessage={sendingMessage}
        />
        <span id="order-note-end" />
      </Box>
    </>}
  </Box>;
}
